import React, { useState, useEffect } from 'react';
import { compressFiles, optimizeFormData, validateFormData } from '../../../shared/utils/performanceUtils';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import { useTitle } from '../../../context/TitleContext';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import LocationPicker from '../../../shared/components/LocationPicker';

import 'react-toastify/dist/ReactToastify.css';
import { motion, AnimatePresence } from 'framer-motion';
import { FaMapMarkerAlt, FaStore, FaEnvelope, FaPhone, FaLock, FaClock, FaArrowLeft, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { addStoreBranch } from '../../../redux/Slices/storeBranchesSlice';

const AddBranch = () => {
    const { setTitle } = useTitle();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, error } = useSelector((state) => state.storeBranches);

    // تحديد نوع المستخدم
    const getUserRole = () => {
        const storedStore = sessionStorage.getItem("storekhadamat");
        if (storedStore) {
            try {
                const parsed = JSON.parse(storedStore);
                return parsed.role;
            } catch {
                return 'store';
            }
        }
        return 'store';
    };

    const userRole = getUserRole();
    
    const [shake, setShake] = useState(false);
    const [phoneError, setPhoneError] = useState(false);
    const [showMap, setShowMap] = useState(false);
    
    // Validation function for phone number
    const isValidPhoneNumber = (phone) => {
        // Remove all non-digit characters
        const cleanedPhone = phone.replace(/\D/g, '');
        // Check if it's a valid Egyptian phone number (11 digits starting with 1)
        // Also accept numbers that start with 20 (from +20) and then 1
        const phoneRegex = /^(1[0-9]{9}|201[0-9]{9})$/;
        return phoneRegex.test(cleanedPhone);
    };
    
    const [formData, setFormData] = useState({
        market_name: '',
        address: '',
        email: '',
        password: '',
        map_link: '',
        start_time: '08:00',
        end_time: '22:00',
        phone: '',
        lat: '',
        lng: '',
        logo: null,
        cover: null
    });

    const [logoPreview, setLogoPreview] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);
    const [imageProcessing, setImageProcessing] = useState(false);
    const [imageStatuses, setImageStatuses] = useState({
        logo: 'idle',
        cover: 'idle'
    });

    useEffect(() => {
        const title = userRole === 'pharmacy' ? 'إضافة فرع صيدلية جديد' : 'إضافة فرع جديد';
        setTitle(title);
    }, [setTitle, userRole]);

    useEffect(() => {
        if (error) {
            setShake(true);
            showErrorToast(error);
            setTimeout(() => setShake(false), 500);
        }
    }, [error]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Special handling for phone number input
        if (name === 'phone') {
            // Allow only digits and limit to 11 characters
            const cleanedValue = value.replace(/\D/g, '').substring(0, 11);
            setFormData(prev => ({
                ...prev,
                [name]: cleanedValue
            }));
            // Clear error when user starts typing
            if (phoneError) {
                setPhoneError(false);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handlePhoneBlur = () => {
        // Only show error if phone has value and is invalid
        if (formData.phone && !isValidPhoneNumber(formData.phone)) {
            setPhoneError(true);
        } else {
            setPhoneError(false);
        }
    };

    const handleLocationChange = (newLat, newLng) => {
        setFormData(prev => ({
            ...prev,
            lat: newLat.toString(),
            lng: newLng.toString()
        }));
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        setImageStatuses(prev => ({ ...prev, logo: 'processing' }));
        if (file) {
            // التحقق من نوع الملف
            if (!file.type.startsWith('image/')) {
                showErrorToast('يرجى اختيار صورة صالحة');
                setImageStatuses(prev => ({ ...prev, logo: 'idle' }));
                return;
            }

            // التحقق من حجم الملف (أقل من 5MB)
            if (file.size > 5 * 1024 * 1024) {
                showErrorToast('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
                setImageStatuses(prev => ({ ...prev, logo: 'idle' }));
            return;
        }

            try {
                setImageProcessing(true);
                // ضغط الصورة
                const [compressedResult] = await compressFiles([file]);
                const compressedFile = compressedResult instanceof File
                    ? compressedResult
                    : new File([compressedResult], file.name, {
                        type: file.type || 'image/jpeg',
                        lastModified: Date.now()
                    });
                
                setFormData(prev => ({
                    ...prev,
                    logo: compressedFile
                }));

                // عرض معاينة الصورة
                const reader = new FileReader();
                reader.onloadend = () => {
                    setLogoPreview(reader.result);
                };
                reader.readAsDataURL(compressedFile);
            } catch (error) {
                showErrorToast('فشل في معالجة الصورة');
                setImageStatuses(prev => ({ ...prev, logo: 'error' }));
            } finally {
                setImageProcessing(false);
                setImageStatuses(prev => ({
                    ...prev,
                    logo: formData.logo ? 'ready' : 'idle'
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                logo: null
            }));
            setLogoPreview(null);
            setImageStatuses(prev => ({ ...prev, logo: 'idle' }));
        }
    };

    const handleRemoveLogo = () => {
        setFormData(prev => ({
            ...prev,
            logo: null
        }));
        setLogoPreview(null);
    };

    const handleCoverChange = async (e) => {
        const file = e.target.files[0];
        setImageStatuses(prev => ({ ...prev, cover: 'processing' }));
        if (file) {
            // التحقق من نوع الملف
            if (!file.type.startsWith('image/')) {
                showErrorToast('يرجى اختيار صورة صالحة');
                setImageStatuses(prev => ({ ...prev, cover: 'idle' }));
                return;
            }

            // التحقق من حجم الملف (أقل من 5MB)
            if (file.size > 5 * 1024 * 1024) {
                showErrorToast('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
                return;
            }

            try {
                setImageProcessing(true);
                // ضغط الصورة
                const [compressedResult] = await compressFiles([file]);
                const compressedFile = compressedResult instanceof File
                    ? compressedResult
                    : new File([compressedResult], file.name, {
                        type: file.type || 'image/jpeg',
                        lastModified: Date.now()
                    });
                
                setFormData(prev => ({
                    ...prev,
                    cover: compressedFile
                }));

                // عرض معاينة الصورة
                const reader = new FileReader();
                reader.onloadend = () => {
                    setCoverPreview(reader.result);
                };
                reader.readAsDataURL(compressedFile);
            } catch (error) {
                showErrorToast('فشل في معالجة الصورة');
                setImageStatuses(prev => ({ ...prev, cover: 'error' }));
            } finally {
                setImageProcessing(false);
                setImageStatuses(prev => ({
                    ...prev,
                    cover: formData.cover ? 'ready' : 'idle'
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                cover: null
            }));
            setCoverPreview(null);
            setImageStatuses(prev => ({ ...prev, cover: 'idle' }));
        }
    };

    const handleRemoveCover = () => {
        setFormData(prev => ({
            ...prev,
            cover: null
        }));
        setCoverPreview(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // التحقق من البيانات المطلوبة
        if (!formData.market_name || !formData.address || !formData.email || !formData.password || !formData.phone) {
            setShake(true);
            showErrorToast('يرجى ملء جميع الحقول المطلوبة');
            setTimeout(() => setShake(false), 500);
            return;
        }

        // التحقق من صحة رقم الهاتف
        if (!isValidPhoneNumber(formData.phone)) {
            setPhoneError(true);
            setShake(true);
            showErrorToast('يرجى إدخال رقم هاتف صحيح (يجب أن يبدأ بـ 1 ويدخل 10 أرقام إضافية)');
            setTimeout(() => setShake(false), 500);
            return;
        }

        try {
            // إعداد البيانات للإرسال
            const dataToSend = new FormData();
            dataToSend.append('market_name', formData.market_name);
            dataToSend.append('address', formData.address);
            dataToSend.append('email', formData.email);
            dataToSend.append('password', formData.password);
            dataToSend.append('phone', '+20' + formData.phone);
            dataToSend.append('map_link', formData.map_link);
            dataToSend.append('start_time', formData.start_time);
            dataToSend.append('end_time', formData.end_time);
            dataToSend.append('lat', formData.lat);
            dataToSend.append('lng', formData.lng);
            if (formData.logo) {
                dataToSend.append('logo', formData.logo);
            }
            if (formData.cover) {
                dataToSend.append('cover', formData.cover);
            }
            
            await dispatch(addStoreBranch(dataToSend)).unwrap();
            showSuccessToast('تم إضافة الفرع بنجاح', {
                autoClose: 1500,
                onClose: () => {
                    navigate('/store-branches');
                }
            });
        } catch (error) {
        }
    };

    const LoadingSpinner = () => (
        <div className="spinner-border text-light" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    return (
        <div className="main_content">
            

            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary d-flex align-items-center"
                        onClick={() => navigate('/store-branches')}
                        style={{ color: '#184194', textDecoration: 'none' }}>
                        <FaArrowLeft className="mx-2" />
                        العودة
                    </motion.button>
                </div>

                {/* Form */}
                <div className="row justify-content-center">
                    <div className="col-12">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className={`card shadow ${shake ? 'shake-animation' : ''}`}
                            style={{ borderRadius: '12px' }}>
                            
                            <div className="card-body p-3">
                                <form onSubmit={handleSubmit}>
                                    {/* اسم الفرع */}
                                    <div className="mb-3">
                                        <label className="form-label fw-medium" style={{ color: '#333' }}>
                                            {userRole === 'pharmacy' ? 'اسم فرع الصيدلية *' : 'اسم الفرع *'}
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="market_name"
                                            value={formData.market_name}
                                            onChange={handleInputChange}
                                            placeholder="أدخل اسم الفرع"
                                            required
                                            style={{
                                                border: '1px solid #e9ecef',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>

                                    {/* شعار الفرع (Logo) */}
                                    <div className="mb-3" style={{ display: 'none' }}>
                                        <label className="form-label fw-medium" style={{ color: '#333' }}>
                                            شعار الفرع (Logo)
                                        </label>
                                        <div className="d-flex align-items-start gap-3">
                                            {/* معاينة الصورة */}
                                            <div>
                                                <div style={{
                                                    width: '120px',
                                                    height: '120px',
                                                    border: '2px dashed #e9ecef',
                                                    borderRadius: '12px',
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: '#f8f9fa',
                                                    position: 'relative'
                                                }}>
                                                    {logoPreview ? (
                                                        <>
                                                            <img
                                                                src={logoPreview}
                                                                alt="Logo Preview"
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={handleRemoveLogo}
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: '5px',
                                                                    right: '5px',
                                                                    backgroundColor: '#dc3545',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '50%',
                                                                    width: '25px',
                                                                    height: '25px',
                                                                    fontSize: '12px',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                                title="حذف الصورة"
                                                            >
                                                                ✕
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <div className="text-muted text-center" style={{ fontSize: '12px' }}>
                                                            <FaStore style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
                                                            <div>لا يوجد شعار</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* زر اختيار الصورة */}
                                            <div className="flex-grow-1">
                                                <input
                                                    type="file"
                                                    id="logoInput"
                                                    accept="image/*"
                                                    onChange={handleLogoChange}
                                                    style={{ display: 'none' }}
                                                />
                                                <label
                                                    htmlFor="logoInput"
                                                    className="btn btn-outline-primary w-100 mb-2"
                                                    style={{
                                                        borderRadius: '8px',
                                                        padding: '10px 16px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    📁 اختيار صورة
                                                </label>
                                                <small className="text-muted d-block">
                                                    • الحجم الأقصى: 5 ميجابايت<br/>
                                                    • الصيغ المدعومة: JPG, PNG, GIF
                                                </small>
                                            </div>
                                        </div>
                                    </div>

                                    {/* صورة الغلاف (Cover) */}
                                    <div className="mb-3">
                                        <label className="form-label fw-medium" style={{ color: '#333' }}>
                                            صورة الغلاف (Cover)
                                        </label>
                                        <div className="d-flex align-items-start gap-3 flex-wrap flex-md-nowrap">
                                            {/* معاينة الصورة */}
                                            <div>
                                                <div
                                                    style={{
                                                        width: '220px',
                                                        height: '130px',
                                                    border: '2px dashed #e9ecef',
                                                    borderRadius: '12px',
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: '#f8f9fa',
                                                    position: 'relative'
                                                    }}
                                                >
                                                    {coverPreview ? (
                                                        <>
                                                            <img
                                                                src={coverPreview}
                                                                alt="Cover Preview"
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={handleRemoveCover}
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: '5px',
                                                                    right: '5px',
                                                                    backgroundColor: '#dc3545',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '50%',
                                                                    width: '26px',
                                                                    height: '26px',
                                                                    fontSize: '13px',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                                title="حذف الصورة"
                                                            >
                                                                ✕
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <div className="text-muted text-center" style={{ fontSize: '12px', padding: '0 12px' }}>
                                                            <FaStore style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
                                                            <div>لم يتم اختيار صورة بعد</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* زر اختيار الصورة */}
                                            <div className="flex-grow-1">
                                                <input
                                                    type="file"
                                                    id="coverInput"
                                                    accept="image/*"
                                                    onChange={handleCoverChange}
                                                    style={{ display: 'none' }}
                                                />
                                                <label
                                                    htmlFor="coverInput"
                                                    className="btn btn-outline-primary w-100 mb-2"
                                                    style={{
                                                        borderRadius: '8px',
                                                        padding: '10px 16px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    🖼️ اختيار صورة الغلاف
                                                </label>
                                                <small className="text-muted d-block">
                                                    • الحجم الأقصى: 5 ميجابايت<br />
                                                    • الصيغ المدعومة: JPG, PNG, GIF<br />
                                                    • النسبة المثالية المقترحة: 16:9
                                                </small>
                                            </div>
                                        </div>
                                        {formData.cover && !coverPreview && (
                                            <div className="text-muted mt-2" style={{ fontSize: '12px' }}>
                                                تم اختيار ملف الغلاف.
                                            </div>
                                        )}
                                    </div>

                                    {/* رقم الهاتف */}
                                    <div className="mb-3">
                                        <label className="form-label fw-medium" style={{ color: '#333' }}>
                                            رقم الهاتف *
                                        </label>
                                        <input
                                            type="tel"
                                            className="form-control"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            onBlur={handlePhoneBlur}
                                            placeholder="1xxxxxxxxx (1 + 10 أرقام)"
                                            required
                                            maxLength="11"
                                            style={{
                                                border: phoneError 
                                                    ? '2px solid #dc3545' 
                                                    : formData.phone && isValidPhoneNumber(formData.phone)
                                                    ? '2px solid #28a745'
                                                    : '1px solid #e9ecef',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                fontSize: '14px'
                                            }}
                                        />
                                        {phoneError && (
                                            <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                                                رقم الهاتف غير صحيح. يجب أن يبدأ بـ 1 ويدخل 10 أرقام إضافية
                                            </div>
                                        )}
                                        {formData.phone && isValidPhoneNumber(formData.phone) && !phoneError && (
                                            <div className="text-success mt-1" style={{ fontSize: '12px' }}>
                                                ✓ رقم الهاتف صحيح
                                            </div>
                                        )}
                                    </div>

                                    {/* البريد الإلكتروني */}
                                    <div className="mb-3">
                                        <label className="form-label fw-medium" style={{ color: '#333' }}>
                                            البريد الإلكتروني *
                                        </label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder="example@email.com"
                                            required
                                            style={{
                                                border: '1px solid #e9ecef',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>

                                    {/* العنوان */}
                                    <div className="mb-3">
                                        <label className="form-label fw-medium" style={{ color: '#333' }}>
                                            العنوان *
                                        </label>
                                        <textarea
                                            className="form-control"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleInputChange}
                                            placeholder="أدخل العنوان الكامل"
                                            required
                                            rows="3"
                                            style={{
                                                border: '1px solid #e9ecef',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                fontSize: '14px',
                                                height: '80px',
                                                resize: 'none'
                                            }}
                                        />
                                    </div>

                                    {/* كلمة المرور */}
                                    <div className="mb-3">
                                        <label className="form-label fw-medium" style={{ color: '#333' }}>
                                            كلمة المرور *
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            placeholder="أدخل كلمة المرور"
                                            required
                                            style={{
                                                border: '1px solid #e9ecef',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>

                                    {/* رابط الخريطة */}
                                    <div className="mb-3">
                                        <label className="form-label fw-medium" style={{ color: '#333' }}>
                                            رابط الخريطة
                                        </label>
                                        <input
                                            type="url"
                                            className="form-control"
                                            name="map_link"
                                            value={formData.map_link}
                                            onChange={handleInputChange}
                                            placeholder="https://maps.google.com/..."
                                            style={{
                                                border: '1px solid #e9ecef',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>

                                    {/* ساعات العمل */}
                                    <div className="row">
                                        <div className="col-6">
                                            <div className="mb-3">
                                                <label className="form-label fw-medium" style={{ color: '#333' }}>
                                                    ساعة البداية *
                                                </label>
                                                <input
                                                    type="time"
                                                    className="form-control"
                                                    name="start_time"
                                                    value={formData.start_time}
                                                    onChange={handleInputChange}
                                                    required
                                                    style={{
                                                        border: '1px solid #e9ecef',
                                                        borderRadius: '8px',
                                                        padding: '12px 16px',
                                                        fontSize: '14px'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="mb-3">
                                                <label className="form-label fw-medium" style={{ color: '#333' }}>
                                                    ساعة النهاية *
                                                </label>
                                                <input
                                                    type="time"
                                                    className="form-control"
                                                    name="end_time"
                                                    value={formData.end_time}
                                                    onChange={handleInputChange}
                                                    required
                                                    style={{
                                                        border: '1px solid #e9ecef',
                                                        borderRadius: '8px',
                                                        padding: '12px 16px',
                                                        fontSize: '14px'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* الإحداثيات - مخفية */}
                                    <div className="row" style={{ display: 'none' }}>
                                        <div className="col-6">
                                            <div className="mb-3">
                                                <label className="form-label fw-medium" style={{ color: '#333' }}>
                                                    خط العرض (Latitude)
                                                </label>
                                                <input
                                                    type="number"
                                                    step="any"
                                                    className="form-control"
                                                    name="lat"
                                                    value={formData.lat}
                                                    onChange={handleInputChange}
                                                    placeholder="31.2001"
                                                    style={{
                                                        border: '1px solid #e9ecef',
                                                        borderRadius: '8px',
                                                        padding: '12px 16px',
                                                        fontSize: '14px'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="mb-3">
                                                <label className="form-label fw-medium" style={{ color: '#333' }}>
                                                    خط الطول (Longitude)
                                                </label>
                                                <input
                                                    type="number"
                                                    step="any"
                                                    className="form-control"
                                                    name="lng"
                                                    value={formData.lng}
                                                    onChange={handleInputChange}
                                                    placeholder="29.9187"
                                                    style={{
                                                        border: '1px solid #e9ecef',
                                                        borderRadius: '8px',
                                                        padding: '12px 16px',
                                                        fontSize: '14px'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* الخريطة التفاعلية */}
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <label className="form-label fw-medium mb-0" style={{ color: '#333' }}>
                                                <FaMapMarkerAlt className="mx-2" />
                                                حدد الموقع على الخريطة
                                            </label>
                                        <motion.button
                                            type="button"
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                className="btn btn-sm btn-outline-primary d-flex align-items-center"
                                                onClick={() => setShowMap(!showMap)}
                                            style={{
                                                borderRadius: '8px',
                                                    padding: '6px 12px'
                                                }}
                                            >
                                                {showMap ? (
                                                    <>
                                                        <FaChevronUp className="mx-1" />
                                                        إخفاء الخريطة
                                                    </>
                                            ) : (
                                                <>
                                                        <FaChevronDown className="mx-1" />
                                                        عرض الخريطة
                                                </>
                                            )}
                                        </motion.button>
                                    </div>

                                        <AnimatePresence>
                                            {showMap && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                >
                                                    <LocationPicker
                                                        lat={formData.lat}
                                                        lng={formData.lng}
                                                        onLocationChange={handleLocationChange}
                                                        height="400px"
                                                    />
                                        <small className="text-muted d-block mt-2">
                                                        ابحث عن المكان في الخريطة أو انقر عليها مباشرة لتحديد الموقع
                                        </small>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {/* زر الإرسال */}
                                    <div className="text-center mt-4">
                                        <motion.button
                                            type="submit"
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className="btn btn-primary px-5 py-2"
                                            disabled={
                                                loading ||
                                                imageProcessing ||
                                                imageStatuses.cover !== 'ready'
                                            }
                                            style={{
                                                backgroundColor: '#184194',
                                                borderColor: '#184194',
                                                borderRadius: '8px',
                                                fontSize: '16px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            {loading ? (
                                                <>
                                                    <LoadingSpinner />
                                                    <span className="ms-2">جاري الإضافة...</span>
                                                </>
                                            ) : (
                                                'إضافة الفرع'
                                            )}
                                        </motion.button>
                                    </div>
                                </form>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddBranch;